// Settings, Integrations (spec C14, Admin only): the sign-in code rules from C1 (docs/plans/sign-in.md,
// PR 9). The AMS360, mailbox, carrier and alert cards join when those connections are built.
// Markup from the Settings board's Integrations tab: an icard per connection, then the sticky footer.
import { useState } from 'react';
import { useLoaderData } from 'react-router';
import type { Api, CodeRulesView } from '../auth/api';
import { PageHeader } from '../shell/PageHeader';
import { useOnline } from '../shell/online';
import { Toast } from '../shell/Toast';
import {
  codeRuleFields,
  defaultsLine,
  draftOf,
  fieldError,
  historyRow,
  integrationsCopy as c,
  isDirty,
  rulesOf,
  type RuleDraft,
} from '../settings/code-rule-lines';
import { HistoryRows } from '../settings/HistoryRows';
import { SettingsNav } from '../settings/SettingsNav';

export type IntegrationsLoaderData = CodeRulesView;

export function IntegrationsPage({ api }: { api: Api }) {
  const loaded = useLoaderData() as IntegrationsLoaderData;
  const [view, setView] = useState(loaded);
  const [draft, setDraft] = useState<RuleDraft>(() => draftOf(loaded.rules));
  const [histOpen, setHistOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ id: number; text: string } | null>(null);

  const dirty = isDirty(draft, view.rules);
  const rules = rulesOf(draft, view.bounds);
  const online = useOnline();
  const canSave = dirty && rules !== null && !busy && online;

  async function save() {
    if (!canSave || !rules) return;
    setBusy(true);
    setError(null);
    const res = await api.saveCodeRules(rules);
    setBusy(false);
    if (!res.ok) {
      setError(c.somethingWrong);
      return;
    }
    setView(res.body);
    setDraft(draftOf(res.body.rules));
    setToast({ id: Date.now(), text: c.saved });
  }

  return (
    <>
      <PageHeader title="Settings" subtitle="Changes apply to everyone in the department" />
      <div className="setwrap">
        <SettingsNav current="/settings/integrations" />
        <div className="setcol">
          <h2 className="dtitle">{c.title}</h2>
          <div className="icard">
            <div className="ih">
              <span className="dot ok" />
              <span className="t14 w6 ink">{c.cardTitle}</span>
              <span className="t12 muted">{c.cardSub}</span>
            </div>
            <div className="row g16 wrap t13 text2" style={{ alignItems: 'flex-start' }}>
              {codeRuleFields.map((f) => {
                const err = fieldError(draft[f.key], view.bounds[f.key]);
                const id = `cr-${f.key}`;
                return (
                  <div key={f.key} className="col g4">
                    <div className="row g8">
                      <label htmlFor={id}>{f.label}</label>
                      <input
                        id={id}
                        className="input numf"
                        type="number"
                        inputMode="numeric"
                        min={view.bounds[f.key].min}
                        max={view.bounds[f.key].max}
                        value={draft[f.key]}
                        aria-invalid={err ? true : undefined}
                        aria-describedby={err ? `${id}-err` : undefined}
                        onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                      />
                      {f.unit && <span>{f.unit}</span>}
                    </div>
                    {err && (
                      <p id={`${id}-err`} className="ferr">
                        {err}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="esub m0">
              {c.cardNote} {defaultsLine(view.defaults)}
            </p>
          </div>
          {histOpen && <HistoryRows rows={view.history.map(historyRow)} empty={c.noHistory} />}
          {error && (
            <p className="ferr" role="alert">
              {error}
            </p>
          )}
          <div className="sfoot">
            <button
              type="button"
              className="btn"
              disabled={!dirty}
              onClick={() => {
                setDraft(draftOf(view.rules));
                setError(null);
              }}
            >
              {c.cancel}
            </button>
            <button
              type="button"
              className="linkbtn"
              aria-expanded={histOpen}
              onClick={() => setHistOpen(!histOpen)}
            >
              {histOpen ? c.hideHistory : c.history(view.history.length)}
            </button>
            <span className="grow" />
            {dirty && <span className="t12 amber">{c.unsaved}</span>}
            <button
              type="button"
              className={dirty && rules ? 'btn btn-primary' : 'btn'}
              disabled={!canSave}
              onClick={save}
            >
              {c.save}
            </button>
          </div>
        </div>
      </div>
      {toast && <Toast key={toast.id} text={toast.text} />}
    </>
  );
}
